import { useEffect, useMemo, useState } from "react";
import { PlatformViewBanner, ManageTenantBtn, TenantColHeader, TenantColCell, usePlatformView } from "@/components/platform-view/PlatformViewShell";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, X, Save, Search, UserPlus, Check, Layers3 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useTenantCountry, pickLocalized } from "@/hooks/useCountryConfig";
import { TrashDeleteAction } from "@/components/trash/TrashDeleteAction";
import { BulkActionBar, RowCheckbox, SelectAllCheckbox, useBulkSelection } from "@/components/bulk/BulkSelection";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";

type P = { id: string; first_name: string; last_name: string; dob: string | null; phone: string | null; email: string | null; person_types: string[]; is_active: boolean; tenant_id?: string | null };

// Phase 1A: creation form is restricted to Student / Guardian / Payer.
// Legacy teacher/staff records remain visible in the list but the create
// form no longer offers those types.
const CREATABLE_TYPES: string[] = ["student", "guardian", "payer"];

export default function People() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState<P[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const canCreate = hasPerm("people.create");
    const { filterTenantId, isPlatformView, tenantName } = usePlatformView();
    const canBulk = hasPerm("people.edit") && hasPerm("trash.manage");
    const bulk = useBulkSelection(`${q}|${filterTenantId}`);
    const bulkRows = items.map((p) => ({ id: p.id, tenant_id: p.tenant_id, tenant_name: tenantName(p.tenant_id) }));

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "100" });
            if (q) params.set("q", q);
            if (filterTenantId) params.set("filter_tenant_id", filterTenantId);
            const { data } = await api.get(`/people?${params.toString()}`);
            setItems(data.items); setTotal(data.total);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [q, filterTenantId]);

    return (
        <div className="space-y-6" data-testid="people-page"><PlatformViewBanner />
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("nav.people")}</h1>
                    <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{total}</p>
                </div>
                {canCreate && <button data-testid="people-new-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}><Plus size={16} /> {t("common.new")}</button>}
            </div>
            <label className="flex items-center gap-2 rounded-md px-3 border max-w-md" style={{ background: "var(--input)", borderColor: "var(--border)", height: 40 }}>
                <Search size={16} style={{ color: "var(--text-muted)" }} />
                <input data-testid="people-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("topbar.search_placeholder")} className="bg-transparent outline-none text-sm w-full" style={{ color: "var(--text)" }} />
            </label>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="people-error">{err}</div>}
            <div className="surface-card overflow-hidden">
                {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div> : items.length === 0 ? (
                    <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="people-empty">{t("common.empty")}</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                            {canBulk && <th className="px-4 py-2 w-8"><SelectAllCheckbox selection={bulk} rows={bulkRows} testid="people-select-all" /></th>}
                            <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                            <TenantColHeader />
                            <th className="px-4 py-2 font-medium">{t("people.types")}</th>
                            <th className="px-4 py-2 font-medium">{t("auth.email")}</th>
                            <th className="px-4 py-2 font-medium">{t("people.phone")}</th>
                            <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                            <th className="px-4 py-2 font-medium text-right"></th>
                        </tr></thead>
                        <tbody>
                            {items.map((p, i) => (
                                <tr key={p.id} onClick={() => nav(`/people/${p.id}`)} className="border-t cursor-pointer hover:bg-[var(--input)] transition-colors" style={{ borderColor: "var(--border)" }} data-testid={`person-row-${p.id}`}>
                                    {canBulk && <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><RowCheckbox selection={bulk} item={bulkRows[i]} testid={`people-select-${p.id}`} /></td>}
                                    <td className="px-4 py-3 font-medium" style={{ color: "var(--text)" }} data-testid={`person-name-${p.id}`}>{p.first_name} {p.last_name}</td>
                                    <TenantColCell tenantId={p.tenant_id} testId={`tenant-cell-person-${p.id}`} />
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">{p.person_types.map((t2) => <span key={t2} className="status-pill status-info">{t(`people.type.${t2}` as any, { defaultValue: t2 })}</span>)}</div>
                                    </td>
                                    <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{p.email || "—"}</td>
                                    <td className="px-4 py-3" style={{ color: "var(--text-muted)" }} onClick={(e) => p.phone && e.stopPropagation()}>
                                        <div className="flex items-center gap-2">{p.phone || "—"}<WhatsAppButton name={`${p.first_name} ${p.last_name}`} phone={p.phone} type="person" id={p.id} testid={`person-wa-btn-${p.id}`} /></div>
                                    </td>
                                    <td className="px-4 py-3"><span className={`status-pill ${p.is_active ? "status-success" : "status-neutral"}`}>{p.is_active ? t("common.active") : t("common.archived")}</span></td>
                                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-2">
                                            <ManageTenantBtn tenantId={(p as any).tenant_id} testId={`manage-person-${p.id}`} nextPath="/people" />
                                            <TrashDeleteAction entityType="person" entityId={p.id}
                                                entityLabel={`${p.first_name} ${p.last_name}`} onArchived={load} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {canBulk && <BulkActionBar selection={bulk} entityType="person" platform={isPlatformView} onDone={load} testidPrefix="people-bulk" />}
            {showNew && <PersonDialog onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
        </div>
    );
}

type StudentLite = { id: string; first_name: string; last_name: string };

function PersonDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
    const { t, i18n } = useTranslation();
    const country = useTenantCountry();
    const [state, setState] = useState({
        first_name: "", last_name: "", email: "", phone: "",
        person_types: ["student"] as string[],
        id_number: "", id_type: "",
        dob: "" as string, gender: "" as string, address: "",
        relation: "other" as string,
        guardian_of: [] as string[],
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    // Phase BUG-1: optional group assignment when creating a student. The
    // enrollment call is best-effort: the person is created regardless, and
    // if the enrollment POST fails we surface a warning but never rollback
    // the person, so the receptionist keeps the record they typed in.
    const [groups, setGroups] = useState<{ id: string; name: string; program_id: string }[]>([]);
    const [groupId, setGroupId] = useState<string>("");
    const [enrollWarn, setEnrollWarn] = useState<string | null>(null);

    const isStudent = state.person_types.includes("student");
    const isGuardian = state.person_types.includes("guardian");
    const isPayer = state.person_types.includes("payer");
    const needsStudentLink = (isGuardian || isPayer) && !isStudent;

    const idLabel = country?.id_field ? pickLocalized(
        { az: country.id_field.label_az, en: country.id_field.label_en, ru: country.id_field.label_ru },
        i18n.language, t("person.id_number")
    ) : t("person.id_number");
    const idHelp = country?.id_field?.help_az && i18n.language.startsWith("az") ? country.id_field.help_az : null;
    const idPattern = country?.id_field?.pattern || undefined;
    const idRequired = !!country?.id_field?.required;
    const phonePrefix = country?.phone_prefix || "";
    useEffect(() => {
        if (phonePrefix && !state.phone) setState((s) => ({ ...s, phone: phonePrefix + " " }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phonePrefix]);

    // Load groups once so the student→group picker can render.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/groups");
                setGroups((data.items || []).map((g: any) => ({ id: g.id, name: g.name, program_id: g.program_id })));
            } catch { /* non-blocking — picker will just be empty */ }
        })();
    }, []);

    const toggleType = (tp: string) => {
        setState((s) => {
            const on = s.person_types.includes(tp);
            const next = on ? s.person_types.filter((x) => x !== tp) : [...s.person_types, tp];
            const stillNeeds = next.includes("guardian") || next.includes("payer");
            return { ...s, person_types: next, guardian_of: stillNeeds ? s.guardian_of : [] };
        });
        // Clear group selection if student is un-picked.
        if (tp === "student" && state.person_types.includes("student")) setGroupId("");
    };

    const submit = async () => {
        setSaving(true); setErr(null); setEnrollWarn(null);
        try {
            if (state.person_types.length === 0) throw new Error(t("person.err.type_not_creatable"));
            if (needsStudentLink && state.guardian_of.length === 0) {
                setErr(t("person.err.student_link_required"));
                setSaving(false);
                return;
            }
            const payload: any = { ...state };
            if (!payload.dob) delete payload.dob;
            if (!payload.gender) delete payload.gender;
            if (!payload.address) payload.address = null;
            if (!isGuardian && !isPayer) {
                payload.guardian_of = [];
            }
            const { data: person } = await api.post("/people", payload);
            // Optional enrollment — never rolls back the person on failure.
            if (isStudent && groupId) {
                const g = groups.find((x) => x.id === groupId);
                if (g) {
                    try {
                        await api.post("/enrollments", {
                            person_id: person.id, program_id: g.program_id,
                            group_id: g.id, status: "active",
                        });
                    } catch (ee: any) {
                        // Person is already created — keep the dialog open
                        // and let the operator retry the enrollment from the
                        // group card, but tell them clearly what happened.
                        setEnrollWarn(t("person.err.enrollment_failed", {
                            defaultValue: "Şəxs yaradıldı, lakin qrupa əlavə edilmədi",
                        }) + ": " + formatApiError(ee));
                        setSaving(false);
                        return;
                    }
                }
            }
            onSaved();
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "student_link_required") setErr(t("person.err.student_link_required"));
            else if (detail?.error === "person_type_not_creatable") setErr(t("person.err.type_not_creatable"));
            else if (Array.isArray(detail) && detail.some((d: any) => String(d.msg || "").includes("dob_in_future"))) setErr(t("person.err.dob_in_future"));
            else setErr(formatApiError(e));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="person-dialog">
            <div className="surface-card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("common.new")}</h2>
                    <button data-testid="person-dialog-close" onClick={onClose} style={{ color: "var(--text-muted)" }}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <F label={t("people.types")}>
                        <div className="flex flex-wrap gap-2">{CREATABLE_TYPES.map((tp) => {
                            const on = state.person_types.includes(tp);
                            return (
                                <button key={tp} type="button" data-testid={`type-${tp}`} onClick={() => toggleType(tp)}
                                    className="rounded-full px-3 py-1 text-xs border inline-flex items-center gap-1"
                                    style={{ background: on ? "var(--selected-bg)" : "var(--input)", color: on ? "var(--text)" : "var(--text-muted)", borderColor: on ? "var(--primary)" : "var(--border)" }}>
                                    {on && <Check size={12} />}
                                    {t(`people.type.${tp}` as any, { defaultValue: tp })}
                                </button>
                            );
                        })}</div>
                        {(isGuardian || isPayer) && <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>{t("person.role_hint")}</div>}
                    </F>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <F label={t("people.first_name")}><input data-testid="person-first-name" value={state.first_name} onChange={(e) => setState({ ...state, first_name: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></F>
                        <F label={t("people.last_name")}><input data-testid="person-last-name" value={state.last_name} onChange={(e) => setState({ ...state, last_name: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></F>
                    </div>
                    <F label={t("auth.email")}><input data-testid="person-email" type="email" value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></F>
                    <F label={country ? `${t("people.phone")} (${phonePrefix})` : t("people.phone")}>
                        <input data-testid="person-phone" value={state.phone} onChange={(e) => setState({ ...state, phone: e.target.value })} placeholder={phonePrefix ? `${phonePrefix} XX XXX XX XX` : undefined}
                            className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    </F>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <F label={t("person.dob")}>
                            <input data-testid="person-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={state.dob} onChange={(e) => setState({ ...state, dob: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                        </F>
                        <F label={t("person.gender")}>
                            <select data-testid="person-gender" value={state.gender} onChange={(e) => setState({ ...state, gender: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }}>
                                <option value="">—</option>
                                <option value="female">{t("person.gender.female")}</option>
                                <option value="male">{t("person.gender.male")}</option>
                                <option value="other">{t("person.gender.other")}</option>
                            </select>
                        </F>
                    </div>
                    <F label={t("person.address")}>
                        <input data-testid="person-address" value={state.address} onChange={(e) => setState({ ...state, address: e.target.value })} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    </F>

                    {(isGuardian || isPayer) && !isStudent && (
                        <StudentPicker
                            selected={state.guardian_of}
                            onChange={(ids) => setState({ ...state, guardian_of: ids })}
                            relation={state.relation}
                            onRelationChange={(r) => setState({ ...state, relation: r })}
                        />
                    )}

                    {isStudent && (
                        <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid="student-group-picker">
                            <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--text)" }}>
                                <Layers3 size={14} /> {t("person.group_optional", { defaultValue: "Qrup (istəyə görə)" })}
                            </div>
                            <select data-testid="person-group-select" value={groupId}
                                onChange={(e) => setGroupId(e.target.value)}
                                className="surface-input px-3 py-2 w-full text-sm"
                                style={{ minHeight: 40 }}>
                                <option value="">— {t("common.none", { defaultValue: "yoxdur" })} —</option>
                                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                                {t("person.group_hint", { defaultValue: "Qrup seçilərsə, şagird yaradıldıqdan sonra avtomatik qeydiyyat aparılır." })}
                            </div>
                        </div>
                    )}

                    {enrollWarn && <div className="text-sm rounded-md p-2" data-testid="person-enroll-warn" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>{enrollWarn}</div>}
                    {err && <div className="text-sm rounded-md p-2" data-testid="person-form-err" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                    <button data-testid="person-save-btn" disabled={saving || !state.first_name || !state.last_name || state.person_types.length === 0} onClick={submit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} {t("common.save")}</button>
                </div>
            </div>
        </div>
    );
}

function StudentPicker({ selected, onChange, relation, onRelationChange }: {
    selected: string[];
    onChange: (ids: string[]) => void;
    relation: string;
    onRelationChange: (r: string) => void;
}) {
    const { t } = useTranslation();
    const [q, setQ] = useState("");
    const [students, setStudents] = useState<StudentLite[]>([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        let alive = true;
        const run = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ type: "student", limit: "30" });
                if (q) params.set("q", q);
                const { data } = await api.get(`/people?${params.toString()}`);
                if (alive) setStudents((data.items || []).map((p: any) => ({ id: p.id, first_name: p.first_name, last_name: p.last_name })));
            } finally { if (alive) setLoading(false); }
        };
        const h = setTimeout(run, 200);
        return () => { alive = false; clearTimeout(h); };
    }, [q]);
    const toggle = (id: string) => {
        onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    };
    const selectedLabels = useMemo(() => {
        const byId: Record<string, StudentLite> = {};
        for (const s of students) byId[s.id] = s;
        return selected.map((id) => byId[id]).filter(Boolean) as StudentLite[];
    }, [selected, students]);
    return (
        <div className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid="student-picker">
            <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--text)" }}>
                <UserPlus size={14} /> {t("person.link_student")} <span className="text-xs" style={{ color: "var(--error-fg)" }}>*</span>
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("person.link_student_hint")}</div>
            <F label={t("person.relation.mother").replace(/^.+$/, "") || ""}>
                <select data-testid="picker-relation" value={relation} onChange={(e) => onRelationChange(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 40 }}>
                    <option value="mother">{t("person.relation.mother")}</option>
                    <option value="father">{t("person.relation.father")}</option>
                    <option value="grandparent">{t("person.relation.grandparent")}</option>
                    <option value="other">{t("person.relation.other")}</option>
                </select>
            </F>
            <label className="flex items-center gap-2 rounded-md px-3 border" style={{ background: "var(--card)", borderColor: "var(--border)", height: 38 }}>
                <Search size={14} style={{ color: "var(--text-muted)" }} />
                <input data-testid="picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("topbar.search_placeholder")}
                    className="bg-transparent outline-none text-sm w-full" style={{ color: "var(--text)" }} />
            </label>
            <div className="max-h-40 overflow-y-auto rounded-md border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                {loading ? <div className="p-3 text-center"><Loader2 className="animate-spin inline" size={14} /></div>
                    : students.length === 0 ? <div className="p-3 text-xs text-center" style={{ color: "var(--text-muted)" }}>{t("person.no_students_found")}</div>
                    : (
                        <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
                            {students.map((s) => {
                                const on = selected.includes(s.id);
                                return (
                                    <li key={s.id}>
                                        <button type="button" data-testid={`pick-student-${s.id}`} onClick={() => toggle(s.id)}
                                            className="w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[var(--input)]"
                                            style={{ color: "var(--text)" }}>
                                            <span>{s.first_name} {s.last_name}</span>
                                            {on && <Check size={14} style={{ color: "var(--primary)" }} />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
            </div>
            {selectedLabels.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="picker-selected">
                    {selectedLabels.map((s) => (
                        <span key={s.id} className="status-pill status-info inline-flex items-center gap-1">
                            {s.first_name} {s.last_name}
                            <button type="button" onClick={() => toggle(s.id)} data-testid={`unpick-${s.id}`} className="hover:opacity-70"><X size={10} /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function F({ label, children }: { label: string; children: React.ReactNode }) { return <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>{children}</label>; }
